import classNames from 'classnames';
import { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { appRoutes } from 'config';
import { CtaButton } from './components/CtaButton';

const POINT_KEYS = ['point1', 'point2', 'point3', 'point4'];

const LOCK = (
  <>
    <rect x="4.5" y="10.5" width="15" height="10" rx="2.5" />
    <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
  </>
);
const USERS = (
  <>
    <circle cx="9" cy="9" r="3.5" />
    <path d="M3 20c0-3.5 2.7-6 6-6s6 2.5 6 6" />
    <circle cx="16.5" cy="8.5" r="2.5" />
    <path d="M21 18c0-2.7-1.7-4.5-4.5-4.5" />
  </>
);
const GLOBE = (
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3c2.5 3 2.5 15 0 18M12 3c-2.5 3-2.5 15 0 18" />
  </>
);

const BOXES: { key: string; statusKey: string; icon: ReactNode }[] = [
  { key: 'recipes', statusKey: 'private', icon: LOCK },
  { key: 'album', statusKey: 'family', icon: USERS },
  { key: 'voice', statusKey: 'private', icon: LOCK },
  { key: 'keepsakes', statusKey: 'shared', icon: GLOBE },
];

export const Section32 = () => {
  const t = useTranslations();
  return (
    <section className={styles.section}>
      <div className={styles.wrap}>
        <div className={styles.copy}>
          <div className={styles.eyebrow}>
            <svg
              viewBox="0 0 24 24"
              className={styles.eyebrowIcon}
              aria-hidden="true"
            >
              {LOCK}
            </svg>
            {t('pages.useCases.memoryKeeping.privacy.eyebrow')}
          </div>
          <h2 className={styles.title}>
            {t.rich('pages.useCases.memoryKeeping.privacy.title', {
              highlight: (chunks) => (
                <span className={styles.highlight}>{chunks}</span>
              ),
            })}
          </h2>
          <p className={styles.lead}>
            {t('pages.useCases.memoryKeeping.privacy.lead')}
          </p>
          <ul className={styles.list}>
            {POINT_KEYS.map((key) => (
              <li key={key} className={styles.item}>
                <span className={styles.check}>
                  <svg
                    viewBox="0 0 24 24"
                    className={styles.checkGlyph}
                    aria-hidden="true"
                  >
                    <path d="m5 12 5 5L20 7" />
                  </svg>
                </span>
                {t(`pages.useCases.memoryKeeping.privacy.${key}`)}
              </li>
            ))}
          </ul>
          <p className={styles.tagline}>
            {t('pages.useCases.memoryKeeping.privacy.tagline')}
          </p>
          <div className={styles.cta}>
            <CtaButton
              variant="primary"
              href={appRoutes.LOGIN}
              reportLocation="Memory Keeping Privacy"
              withArrow
            >
              {t('pages.useCases.memoryKeeping.privacy.cta')}
            </CtaButton>
          </div>
        </div>

        {/* Privacy viz */}
        <div className={styles.viz} aria-hidden="true">
          <span className={styles.vizLock}>
            <svg viewBox="0 0 24 24" className={styles.vizLockGlyph}>
              {LOCK}
            </svg>
          </span>
          <div className={styles.vizList}>
            {BOXES.map((box) => (
              <div key={box.key} className={styles.vizRow}>
                <span className={styles.vizIcon}>
                  <svg viewBox="0 0 24 24" className={styles.vizGlyph}>
                    {box.icon}
                  </svg>
                </span>
                <span className={styles.vizLabel}>
                  {t(`pages.useCases.memoryKeeping.privacy.boxes.${box.key}`)}
                </span>
                <span className={styles.vizStatus}>
                  {t(
                    `pages.useCases.memoryKeeping.privacy.status.${box.statusKey}`,
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const styles = {
  section: 'marketing-dark:bg-mf-navy pt-[74px] pb-5',
  wrap: 'mx-auto grid max-w-[1200px] grid-cols-1 items-center gap-12 px-6 lg:grid-cols-2 lg:gap-16 lg:px-10',
  copy: 'text-center lg:text-left',
  eyebrow:
    'mb-4 inline-flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.15em] text-mf-blue marketing-dark:text-mf-coral',
  eyebrowIcon: 'h-4 w-4 fill-none stroke-current stroke-2',
  title:
    'font-inter text-3xl font-extrabold leading-[1.1] tracking-[-0.02em] text-mf-off-black-600 marketing-dark:text-white sm:text-[40px]',
  highlight: 'text-mf-blue marketing-dark:text-mf-coral',
  lead: 'mx-auto mt-6 max-w-[480px] text-lg leading-[1.6] text-mf-gray-600 marketing-dark:text-white/72 lg:mx-0 lg:text-xl',
  list: 'mx-auto mt-8 flex max-w-[480px] flex-col gap-4 text-left lg:mx-0',
  item: 'flex items-start gap-3 text-[15px] font-medium text-mf-off-black-600 marketing-dark:text-white/90',
  check: classNames(
    'mt-0.5 flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full',
    'bg-[#EBF0F7] text-mf-blue marketing-dark:bg-white/10 marketing-dark:text-white',
  ),
  checkGlyph: 'h-3.5 w-3.5 fill-none stroke-current stroke-[2.4]',
  tagline:
    'mt-8 text-lg font-bold text-mf-off-black-600 marketing-dark:text-white',
  cta: 'mt-6 flex justify-center lg:justify-start',
  // viz — soft blue-tinted panel on the light page, deep navy gradient on dark.
  viz: classNames(
    'relative flex min-h-[400px] flex-col justify-end overflow-hidden rounded-[24px] border p-10',
    'border-[#F0F0F0] bg-[linear-gradient(180deg,#EBF0F7,#ffffff)]',
    'marketing-dark:border-white/10 marketing-dark:bg-[linear-gradient(180deg,#13234f,#0a1430)]',
  ),
  vizLock: classNames(
    'absolute right-10 top-10 flex h-[90px] w-[90px] items-center justify-center rounded-[24px] border',
    'border-[#F0F0F0] bg-white text-mf-blue shadow-[0_8px_22px_rgba(20,30,60,0.06)] marketing-dark:shadow-none',
    'marketing-dark:border-white/15 marketing-dark:bg-white/[0.06] marketing-dark:text-white',
  ),
  vizLockGlyph: 'h-8 w-8 fill-none stroke-current stroke-[1.6]',
  vizList: 'flex flex-col gap-2.5',
  vizRow: classNames(
    'flex items-center gap-3 rounded-[12px] border px-4 py-3 text-sm',
    'border-[#F0F0F0] bg-white text-mf-off-black-600',
    'marketing-dark:border-white/10 marketing-dark:bg-white/[0.06] marketing-dark:text-white/90',
  ),
  vizIcon: classNames(
    'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md',
    'bg-[#EBF0F7] text-mf-blue marketing-dark:bg-white/10 marketing-dark:text-white',
  ),
  vizGlyph: 'h-3.5 w-3.5 fill-none stroke-current stroke-2',
  vizLabel: 'flex-1 font-medium',
  vizStatus:
    'text-[11px] font-bold uppercase tracking-[0.06em] text-mf-blue marketing-dark:text-mf-coral',
};
